import { useCallback, useEffect, useState } from 'react';
import { useSnackbar } from 'notistack';

import HttpClient from '../../shared/infrastructures/clients/http.client';
import { extractTranslatedApiErrorMessage } from '../../shared/utils/translate-api-error.util';
import { DeliveryCity } from '../types/delivery-city.type';
import { ADMIN_DELIVERY_CITIES_API_PATHS } from '../constants/delivery-cities-api-paths.constants';
import { sortSlots } from '../utils/delivery-city-form-rules.util';

type UseAdminGetDeliveryCityByIdResult = {
  isLoading: boolean;
  isRefetching: boolean;
  deliveryCity: DeliveryCity | null;
  refetch: () => Promise<void>;
};

const INITIAL_STATE: Omit<UseAdminGetDeliveryCityByIdResult, 'refetch'> = {
  isLoading: true,
  isRefetching: false,
  deliveryCity: null,
};

const withSortedSlots = (deliveryCity: DeliveryCity): DeliveryCity => ({
  ...deliveryCity,
  slots: sortSlots(deliveryCity.slots),
});

export function useAdminGetDeliveryCityById({
  deliveryCityId,
}: {
  deliveryCityId: string;
}): UseAdminGetDeliveryCityByIdResult {
  const { enqueueSnackbar } = useSnackbar();
  const [result, setResult] = useState(INITIAL_STATE);

  const refetch = useCallback(async (): Promise<void> => {
    setResult((previous) => (previous.isLoading ? previous : { ...previous, isRefetching: true }));

    try {
      const response = await HttpClient.get<DeliveryCity | undefined>(
        ADMIN_DELIVERY_CITIES_API_PATHS.GET_DELIVERY_CITY_BY_ID(deliveryCityId)
      );

      setResult({ isLoading: false, isRefetching: false, deliveryCity: response ? withSortedSlots(response) : null });
    } catch (error) {
      enqueueSnackbar(
        extractTranslatedApiErrorMessage(
          error,
          'Une erreur est survenue lors de la récupération de la ville de livraison'
        ),
        { variant: 'error' }
      );
      setResult((previous) => ({ ...previous, isLoading: false, isRefetching: false }));
    }
  }, [deliveryCityId, enqueueSnackbar]);

  useEffect(() => {
    setResult(INITIAL_STATE);
  }, [deliveryCityId]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...result, refetch };
}
